import { useCallback, useState } from "react";
import type { U3125ArAgingRequest } from "./U3125Service";
import { u3125CanExportArAging } from "./u3125CanExportArAging";

export interface UseU3125ArAgingExportHandlersProps {
  doExportArAgingExcelFn: (body: U3125ArAgingRequest) => Promise<unknown>;
  doExportArAgingPdfFn: (body: U3125ArAgingRequest) => Promise<unknown>;
  onExcelSuccess: () => void;
  onPdfSuccess: () => void;
  onError: (params: { error: unknown }) => void;
}

export function useU3125ArAgingExportHandlers({
  doExportArAgingExcelFn,
  doExportArAgingPdfFn,
  onExcelSuccess,
  onPdfSuccess,
  onError,
}: UseU3125ArAgingExportHandlersProps) {
  const [isExportingExcel, setIsExportingExcel] = useState(false);
  const [isExportingPdf, setIsExportingPdf] = useState(false);

  const exportExcel = useCallback(
    async (body: U3125ArAgingRequest) => {
      if (!u3125CanExportArAging(body)) return;
      setIsExportingExcel(true);
      try {
        await doExportArAgingExcelFn(body);
        onExcelSuccess();
      } catch (error: unknown) {
        onError({ error });
      } finally {
        setIsExportingExcel(false);
      }
    },
    [doExportArAgingExcelFn, onExcelSuccess, onError],
  );

  const exportPdf = useCallback(
    async (body: U3125ArAgingRequest) => {
      if (!u3125CanExportArAging(body)) return;
      setIsExportingPdf(true);
      try {
        await doExportArAgingPdfFn(body);
        onPdfSuccess();
      } catch (error: unknown) {
        onError({ error });
      } finally {
        setIsExportingPdf(false);
      }
    },
    [doExportArAgingPdfFn, onPdfSuccess, onError],
  );

  return { exportExcel, exportPdf, isExportingExcel, isExportingPdf };
}
